import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Chat from './pages/Chat';
import UserChat from './pages/UserChat';
import Login from './pages/Login';
import Register from './pages/Register';
import Onboarding from './pages/Onboarding';
import Home from './pages/Home';
import Layout from './components/Layout';
import AISearch from './pages/AISearch';
import AIAnswer from './pages/AIAnswer';
import JobsiteDeepDive from './pages/JobsiteDeepDive';
import Jobsite25_111Search from './pages/Jobsite25-111Search'; // SharePoint Analyst (25-111 data)
import VistaERD from './pages/VistaERD';
import BiddingIntelligence from './pages/BiddingIntelligence';
import WindsorMarketIntelligence from './pages/WindsorMarketIntelligence';
import UserGuide from './pages/UserGuide';
import SylvanFeed from './pages/SylvanFeed';
import CRMAnalyst from './pages/CRMAnalyst';
// import SafetyAnalyst from './pages/SafetyAnalyst';
import AIReport from './pages/AIReport';
import AIWorkflow from './pages/AIWorkflow';
import ContractRedline from './pages/ContractRedline';
import NdaAgent from './pages/NdaAgent';
import UsageDashboard from './pages/UsageDashboard';
import LaborEstimator from './pages/LaborEstimator';
import JoboticsAgent from './pages/JoboticsAgent';
import MarkupStudio from './pages/MarkupStudio';
import MarkupStudioList from './pages/MarkupStudioList';
import RsiAgentList from './pages/RsiAgentList';
import RsiAgentBuilder from './pages/RsiAgentBuilder';
import AgentRunViewer from './pages/AgentRunViewer';
import AgentStats from './pages/AgentStats';
import DigitalTwinDemo from './pages/DigitalTwinDemo';
import BCSCarolinas from './pages/BCSCarolinas';
import SkillGrid from './pages/SkillGrid';
import Deck from './pages/Deck';
import ClientDeck from './pages/ClientDeck';
import OwnerDeck from './pages/OwnerDeck';
import PartnerDeck from './pages/PartnerDeck';
import Gallery from './pages/Gallery';
import DiscoverNormKing from './pages/DiscoverNormKing';
import DiscoverDelta from './pages/DiscoverDelta';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PageHeaderProvider } from './contexts/PageHeaderContext';
import CrmAnalystNew from './pages/crmAnalystNew';
import SafetyAnalyst from './pages/safetyAnalyst';
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import TemplateChatbot from './pages/TemplateChatbot';
import TemplateFileUpload from './pages/TemplateFileUpload';
import Toast from './components/Toast';
import { canAccessDigitalTwinDemo } from './config/digitalTwinDemoAccess';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sylvan-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // New-account onboarding gate: an authenticated user who hasn't completed
  // their profile is sent to the role/challenge flow before the home page.
  //
  // NON-TRAPPING by design: the gate also clears once THIS user has been
  // through onboarding this session. The flag is keyed PER USER ID — a global
  // flag would suppress the gate for every later new-account login in the
  // same browser session (which is exactly the bug we hit). So if the profile
  // save fails for any reason — backend unreachable, transient 500 — that one
  // user still reaches the app instead of bouncing here forever, while a
  // different new account still gets gated. A successful save persists
  // profileCompletedAt, so future logins skip onboarding outright.
  let onboardedThisSession = false;
  try {
    onboardedThisSession = !!(user && sessionStorage.getItem('jb_onboarded_' + user.id) === '1');
  } catch (_) { /* private mode */ }
  if (user && !user.profileCompletedAt && !onboardedThisSession && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Sylvan Feed Protected Route Component (with user authorization)
const SylvanFeedProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  // Define allowed users for Sylvan Feed demo
  const allowedSylvanFeedUsers = [
    'rdong@sylvan-inc.com',
    'acumming@sylvan-inc.com', 
    'anita@e-3.tech'
  ];
  
  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sylvan-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user is authorized for Sylvan Feed
  if (user && !allowedSylvanFeedUsers.includes(user.email)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="mb-6">
            <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h1>
            <p className="text-gray-600 mb-6">
              The Sylvan Feed demo is currently only available to authorized users. 
              Please contact your administrator if you believe you should have access.
            </p>
            <div className="bg-gray-100 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                <strong>Current user:</strong> {user.email}
              </p>
            </div>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-sylvan-600 hover:bg-sylvan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return children;
};

// Digital twin demo — Jobotics live project dataset (allowlisted emails only)
const DigitalTwinDemoProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sylvan-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const allowed = canAccessDigitalTwinDemo(user?.email);

  if (user && !allowed) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="mb-6">
            <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h1>
            <p className="text-gray-600 mb-6">
              The demo live projects dashboard is only available to authorized Jobotics accounts.
            </p>
            <div className="bg-gray-100 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                <strong>Current user:</strong> {user.email}
              </p>
            </div>
            <Link
              to="/jobotics-agent"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-sylvan-600 hover:bg-sylvan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500"
            >
              Back to Jobotics Agent
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

// Main App Component
function AppContent() {
  // Back-navigation confirm only — close/refresh closes directly (demo UX)
  React.useEffect(() => {
    const handlePopState = (e) => {
      // Show warning when user tries to go back
      if (!window.confirm('Are you sure you want to go back? Your work may be lost.')) {
        e.preventDefault();
        window.history.pushState(null, null, window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);

    // Push initial state to enable popstate detection
    window.history.pushState(null, null, window.location.href);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return (
    <Router>
      <ThemeProvider>
        <div className="App">
          <Toast />
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
        
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          {/* Public routes (no login required) */}
          <Route path="/deck" element={<Deck />} />
          <Route path="/client-deck" element={<ClientDeck />} />
          <Route path="/owner-deck" element={<OwnerDeck />} />
          <Route path="/partner-deck" element={<PartnerDeck />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/discover-normking" element={<DiscoverNormKing />} />
          <Route path="/discover-delta" element={<DiscoverDelta />} />
          <Route path="/bcs-carolinas" element={<BCSCarolinas />} />
          <Route path="/skill-grid" element={<SkillGrid />} />
          
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          } />

          <Route path="/" element={
            <ProtectedRoute>
              <Layout>
                <Home />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/sylvan-feed" element={
            <SylvanFeedProtectedRoute>
              <Layout>
                <SylvanFeed />
              </Layout>
            </SylvanFeedProtectedRoute>
          } />
          
          <Route path="/chat" element={
            <ProtectedRoute>
              <Layout>
                <Chat />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/user-chat" element={
            <ProtectedRoute>
              <UserChat />
            </ProtectedRoute>
          } />
          
          <Route path="/ai-search" element={
            <ProtectedRoute>
              <Layout>
                <AISearch />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/contract-review" element={
            <ProtectedRoute>
              <Navigate to="/contract-redline" replace />
            </ProtectedRoute>
          } />

          <Route path="/contract-redline" element={
            <ProtectedRoute>
              <Layout>
                <ContractRedline />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/nda-agent" element={
            <ProtectedRoute>
              <Layout>
                <NdaAgent />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/ai-answer" element={
            <ProtectedRoute>
              <Layout>
                <AIAnswer />
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* /voice-agent retired in Phase 2: voice input is now a mic button in /chat. */}

          <Route path="/jobsite-deep-dive" element={
            <ProtectedRoute>
              <Layout>
                <JobsiteDeepDive />
              </Layout>
            </ProtectedRoute>
          } />
          
          {/* SharePoint Analyst (25-111 data) */}
          <Route path="/25-111-search" element={
            <ProtectedRoute>
              <Layout>
                <Jobsite25_111Search />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/vista-erd" element={
            <ProtectedRoute>
              <Layout>
                <VistaERD />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/bidding-intelligence" element={
            <ProtectedRoute>
              <Layout>
                <BiddingIntelligence />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/windsor-market-intelligence" element={
            <ProtectedRoute>
              <Layout>
                <WindsorMarketIntelligence />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/crm-analyst" element={
            <ProtectedRoute>
              <Layout>
                <CRMAnalyst />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/safety-analyst" element={
            <ProtectedRoute>
              <Layout>
                <SafetyAnalyst />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/ai-report" element={
            <ProtectedRoute>
              <Layout>
                <AIReport />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/ai-workflow" element={
            <ProtectedRoute>
              <Layout>
                <AIWorkflow />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/user-guide" element={
            <ProtectedRoute>
              <Layout>
                <UserGuide />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/usage-dashboard" element={
            <ProtectedRoute>
              <Layout>
                <UsageDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/labor-estimator" element={
            <ProtectedRoute>
              <Layout>
                <LaborEstimator />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/jobotics-agent" element={
            <ProtectedRoute>
              <Layout>
                <JoboticsAgent />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/markup-studio" element={
            <ProtectedRoute>
              <Layout>
                <MarkupStudioList />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/markup-studio/:sessionId" element={
            <ProtectedRoute>
              <Layout>
                <MarkupStudio />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/rsi-agents" element={
            <ProtectedRoute>
              <Layout>
                <RsiAgentList />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/rsi-agents/new" element={
            <ProtectedRoute>
              <Layout>
                <RsiAgentBuilder />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/rsi-agents/:id/edit" element={
            <ProtectedRoute>
              <Layout>
                <RsiAgentBuilder />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/runs/:runId" element={
            <ProtectedRoute>
              <Layout>
                <AgentRunViewer />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/agents/:id/stats" element={
            <ProtectedRoute>
              <Layout>
                <AgentStats />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/digital-twin-demo" element={
            <DigitalTwinDemoProtectedRoute>
              <Layout>
                <DigitalTwinDemo />
              </Layout>
            </DigitalTwinDemoProtectedRoute>
          } />
          
          {/* Sample Template Pages */}
          <Route path="/template/dashboard" element={
            <ProtectedRoute>
              <PageHeaderProvider>
                <Layout>
                  <Dashboard />
                </Layout>
              </PageHeaderProvider>
            </ProtectedRoute>
          } />
          
          <Route path="/template/analytics" element={
            <ProtectedRoute>
              <PageHeaderProvider>
                <Layout>
                  <Analytics />
                </Layout>
              </PageHeaderProvider>
            </ProtectedRoute>
          } />
          
          <Route path="/template/settings" element={
            <ProtectedRoute>
              <PageHeaderProvider>
                <Layout>
                  <Settings />
                </Layout>
              </PageHeaderProvider>
            </ProtectedRoute>
          } />
          
          <Route path="/template/chatbot" element={
            <ProtectedRoute>
              <PageHeaderProvider>
                <Layout>
                  <TemplateChatbot />
                </Layout>
              </PageHeaderProvider>
            </ProtectedRoute>
          } />
          
          <Route path="/template/file-upload" element={
            <ProtectedRoute>
              <PageHeaderProvider>
                <Layout>
                  <TemplateFileUpload />
                </Layout>
              </PageHeaderProvider>
            </ProtectedRoute>
          } />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </div>
      </ThemeProvider>
    </Router>
  );
}

// App with Auth Provider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App; 